import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../api';
import TrainerLayout from './TrainerLayout';
import { TrainerAntragBadge, TrainerAntragWarnung } from './TrainerAntrag';

const KURS_LABELS = {
  pirat: '🏴‍☠️ Pirat', seepferdchen: '🐴 Seepferdchen', trixi: '🌟 Trixi',
  bronze: '🥉 Bronze', silber: '🥈 Silber', gold: '🥇 Gold',
  hai: '🦈 Hai', la_silber: '🦈 LA Silber', la_gold: '🦈 LA Gold',
};
const WOCHENTAGE = ['Montag','Dienstag','Mittwoch','Donnerstag','Freitag','Samstag','Sonntag'];

// Datumswerte kommen als 'YYYY-MM-DD'; Mittag in UTC verhindert ein Verrutschen des Tages
function fmtDatum(d) {
  if (!d) return '';
  return new Date(`${String(d).slice(0, 10)}T12:00:00Z`)
    .toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit', year: '2-digit' });
}
// 0 = Montag … 6 = Sonntag (wie gruppe_termine)
const dbWochentag = (iso) => (new Date(`${String(iso).slice(0, 10)}T12:00:00Z`).getUTCDay() + 6) % 7;
const terminText = (t) => `${WOCHENTAGE[t.wochentag]} ${t.uhrzeit_von?.slice(0, 5)}${t.uhrzeit_bis ? `–${t.uhrzeit_bis.slice(0, 5)}` : ''} Uhr`;
function fmtUhrzeit(ts) {
  if (!ts) return '';
  return new Date(ts).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
}

export default function TrainerGruppe() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [gruppe, setGruppe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [starting, setStarting] = useState(false);
  const [nachtragOffen, setNachtragOffen] = useState(false);
  const [nachtragDatum, setNachtragDatum] = useState('');
  const [tab, setTab] = useState('uebersicht'); // 'uebersicht' | 'fortschritt'
  const [fortschritt, setFortschritt] = useState(null);
  const [fortschrittLoading, setFortschrittLoading] = useState(false);
  const [fortschrittFehler, setFortschrittFehler] = useState('');
  const [inArbeit, setInArbeit] = useState({}); // `${schwimmer_id}_${uebung_id}` → true

  const load = () => {
    setLoading(true);
    api.get(`/api/trainer/gruppen/${id}`)
      .then(r => setGruppe(r.data))
      .catch(() => setError('Gruppe konnte nicht geladen werden'))
      .finally(() => setLoading(false));
  };

  const loadFortschritt = () => {
    if (fortschritt) return;
    setFortschrittLoading(true);
    api.get(`/api/trainer/gruppen/${id}/fortschritt`)
      .then(r => setFortschritt(r.data))
      .catch(() => {})
      .finally(() => setFortschrittLoading(false));
  };

  useEffect(() => { load(); }, [id]);

  // Übung direkt in der Tabelle abhaken bzw. zurücknehmen (sofort sichtbar, bei Fehler zurückgesetzt)
  const toggleUebung = async (m, u) => {
    const schluessel = `${m.schwimmer_id}_${u.id}`;
    if (inArbeit[schluessel]) return;
    const vorher = m.abgeschlossen[u.id] || null;
    if (vorher && !window.confirm(`„${u.titel}" bei ${m.vorname} ${m.nachname} wieder als offen markieren?`)) return;

    const setze = (wert) => setFortschritt(prev => ({
      ...prev,
      mitglieder: prev.mitglieder.map(x => {
        if (x.schwimmer_id !== m.schwimmer_id) return x;
        const abgeschlossen = { ...x.abgeschlossen };
        if (wert) abgeschlossen[u.id] = wert; else delete abgeschlossen[u.id];
        return { ...x, abgeschlossen };
      }),
    }));

    setFortschrittFehler('');
    setInArbeit(p => ({ ...p, [schluessel]: true }));
    setze(vorher ? null : new Date().toISOString());
    try {
      const url = `/api/trainer/schwimmer/${m.schwimmer_id}/uebungen/${u.id}`;
      if (vorher) await api.delete(url);
      else await api.post(url, {});
    } catch (err) {
      setze(vorher);
      setFortschrittFehler(err.response?.data?.error || 'Speichern fehlgeschlagen – bitte erneut versuchen.');
    } finally {
      setInArbeit(p => { const n = { ...p }; delete n[schluessel]; return n; });
    }
  };

  const handleTabChange = (t) => {
    setTab(t);
    if (t === 'fortschritt') loadFortschritt();
  };

  const handleStartEinheit = async (datum) => {
    setStarting(true);
    try {
      const res = await api.post('/api/trainer/einheiten', { gruppe_id: Number(id), ...(datum ? { datum } : {}) });
      navigate(`/trainer/einheiten/${res.data.id}`);
    } catch (err) {
      setError(err.response?.data?.error || 'Training konnte nicht erfasst werden');
      setStarting(false);
    }
  };

  const heute = new Date().toLocaleDateString('sv-SE');
  const heutigeEinheit = gruppe?.einheiten?.find(e => String(e.datum).slice(0, 10) === heute);
  // An welchen Tagen trainiert diese Gruppe? Ein Training lässt sich nur an einem Trainingstag starten.
  const termineAm = (iso) => (gruppe?.termine || []).filter(t => t.wochentag === dbWochentag(iso));
  const heuteTrainingstag = termineAm(heute).length > 0;
  const keinTerminHinweis = (iso) => (
    <div style={{
      background: '#fff8e1', color: '#e65100', borderRadius: 10,
      padding: '0.6rem 0.8rem', fontSize: '0.8rem', marginBottom: '0.5rem',
    }}>
      {WOCHENTAGE[dbWochentag(iso)]} ist für <strong>{gruppe.name}</strong> kein Trainingstag –
      deshalb lässt sich heute kein Training starten.
      <div style={{ color: '#555', marginTop: 2 }}>
        {(gruppe.termine || []).length
          ? <>Trainingszeiten: {gruppe.termine.map(terminText).join(' · ')}</>
          : <>Für diese Gruppe ist noch kein Trainingstermin hinterlegt. Bitte dem Schwimmadmin melden.</>}
      </div>
    </div>
  );

  return (
    <TrainerLayout title={gruppe?.name || 'Gruppe'} backTo="/trainer/gruppen">
      {loading && (
        <div style={{ textAlign: 'center', padding: '3rem' }}>
          <div className="loading-spinner" style={{ margin: '0 auto' }} />
        </div>
      )}

      {error && <div className="alert alert-error">{error}</div>}

      {gruppe && !loading && (
        <>
          {/* Info-Karte */}
          <div style={{
            background: '#fff', borderRadius: 12, padding: '1rem',
            marginBottom: '0.875rem', boxShadow: '0 1px 4px rgba(0,0,0,0.07)',
            display: 'flex', flexDirection: 'column', gap: '0.35rem',
          }}>
            <div style={{ fontWeight: 700, fontSize: '1.1rem' }}>{KURS_LABELS[gruppe.kurs_typ] || gruppe.kurs_typ}</div>
            {gruppe.trainer_name && <div style={{ fontSize: '0.85rem', color: '#555' }}>👤 {gruppe.trainer_name}</div>}
            {gruppe.ort && <div style={{ fontSize: '0.85rem', color: '#555' }}>📍 {gruppe.ort}</div>}
            <div style={{ fontSize: '0.85rem', color: '#555' }}>👥 {gruppe.mitglieder?.length || 0} Schwimmer</div>
            {(gruppe.termine || []).map(t => (
              <div key={t.id} style={{ fontSize: '0.85rem', color: '#1565c0', fontWeight: 600 }}>
                📅 {WOCHENTAGE[t.wochentag]} {t.uhrzeit_von?.slice(0,5)}{t.uhrzeit_bis ? `–${t.uhrzeit_bis.slice(0,5)}` : ''} Uhr
              </div>
            ))}
          </div>

          <TrainerAntragWarnung kinder={gruppe.mitglieder} />

          {/* Training von heute öffnen oder starten */}
          {termineAm(heute).length === 0 && keinTerminHinweis(heute)}
          {heutigeEinheit ? (
            <button onClick={() => navigate(`/trainer/einheiten/${heutigeEinheit.id}`)}
              style={{
                width: '100%', background: '#4caf50', color: '#fff', border: 'none',
                borderRadius: 12, padding: '1rem', fontWeight: 700, fontSize: '1rem',
                cursor: 'pointer', marginBottom: '0.5rem', boxShadow: '0 2px 6px rgba(76,175,80,0.35)',
              }}>
              ▶ Training von heute öffnen ({fmtUhrzeit(heutigeEinheit.gestartet_am)} Uhr)
            </button>
          ) : (
            // Starten geht nur am Trainingstag der Gruppe – sonst entstehen Trainings ohne Trainingszeit
            <button onClick={() => handleStartEinheit()} disabled={starting || !heuteTrainingstag}
              title={heuteTrainingstag ? undefined : `${gruppe.name} trainiert ${WOCHENTAGE[dbWochentag(heute)].toLowerCase()}s nicht`}
              style={{
                width: '100%', border: 'none',
                background: heuteTrainingstag ? '#1565c0' : '#eceff1',
                color: heuteTrainingstag ? '#fff' : '#90a4ae',
                borderRadius: 12, padding: '1rem', fontWeight: 700, fontSize: '1rem',
                cursor: heuteTrainingstag ? 'pointer' : 'not-allowed',
                marginBottom: '0.5rem', opacity: starting ? 0.7 : 1,
                boxShadow: heuteTrainingstag ? '0 2px 6px rgba(21,101,192,0.35)' : 'none',
              }}>
              {starting ? 'Moment…' : heuteTrainingstag ? '🏁 Training starten' : '🏁 Heute kein Training'}
            </button>
          )}

          {/* Nachträglich erfassen */}
          {!nachtragOffen ? (
            <button onClick={() => { setNachtragOffen(true); setNachtragDatum(heute); }}
              style={{
                width: '100%', background: '#fff', color: '#1565c0', border: '1px solid #e0e0e0',
                borderRadius: 12, padding: '0.7rem', fontWeight: 600, fontSize: '0.9rem',
                cursor: 'pointer', marginBottom: '0.875rem',
              }}>
              📅 Training nachtragen
            </button>
          ) : (
            <div style={{ background: '#fff', borderRadius: 12, padding: '0.875rem', marginBottom: '0.875rem', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
              <div style={{ fontWeight: 700, fontSize: '0.9rem', marginBottom: '0.5rem' }}>Training nachtragen</div>
              <div style={{ fontSize: '0.8rem', color: '#666', marginBottom: '0.6rem' }}>
                Für welchen Tag? Anwesenheit und Team lassen sich danach wie gewohnt erfassen.
              </div>
              <input type="date" value={nachtragDatum} max={heute} onChange={e => setNachtragDatum(e.target.value)}
                style={{ width: '100%', padding: '0.6rem', fontSize: '16px', border: '1px solid #ddd', borderRadius: 8, boxSizing: 'border-box', marginBottom: '0.6rem' }} />
              {nachtragDatum && (
                termineAm(nachtragDatum).length > 0 ? (
                  <div style={{ fontSize: '0.8rem', color: '#2e7d32', marginBottom: '0.6rem' }}>
                    ✓ {termineAm(nachtragDatum).map(terminText).join(' · ')}
                  </div>
                ) : (
                  <div style={{ fontSize: '0.8rem', color: '#e65100', marginBottom: '0.6rem' }}>
                    ⚠ {WOCHENTAGE[dbWochentag(nachtragDatum)]} ist kein Trainingstag dieser Gruppe – Stunden lassen sich für diesen Tag nicht erfassen.
                  </div>
                )
              )}
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button onClick={() => setNachtragOffen(false)}
                  style={{ flex: 1, padding: '0.7rem', background: '#fff', border: '1px solid #e0e0e0', borderRadius: 10, cursor: 'pointer' }}>
                  Abbrechen
                </button>
                <button onClick={() => handleStartEinheit(nachtragDatum)} disabled={starting || !nachtragDatum}
                  style={{ flex: 2, padding: '0.7rem', background: '#1565c0', color: '#fff', border: 'none', borderRadius: 10, fontWeight: 700, cursor: 'pointer' }}>
                  {starting ? 'Moment…' : 'Training öffnen'}
                </button>
              </div>
            </div>
          )}

          {/* Tab-Leiste */}
          <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.875rem' }}>
            {['uebersicht', 'fortschritt'].map(t => (
              <button key={t} onClick={() => handleTabChange(t)}
                style={{
                  flex: 1, padding: '0.6rem', border: 'none', borderRadius: 10, cursor: 'pointer',
                  fontWeight: 700, fontSize: '0.85rem',
                  background: tab === t ? '#1565c0' : '#fff',
                  color: tab === t ? '#fff' : '#555',
                  boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                }}>
                {t === 'uebersicht' ? '👥 Übersicht' : '📋 Fortschritt'}
              </button>
            ))}
          </div>

          {tab === 'uebersicht' && (<>
            {/* Mitgliederliste */}
            <div style={{ background: '#fff', borderRadius: 12, marginBottom: '0.875rem', boxShadow: '0 1px 4px rgba(0,0,0,0.07)', overflow: 'hidden' }}>
              <div style={{ padding: '0.75rem 1rem', fontWeight: 700, fontSize: '0.9rem', borderBottom: '1px solid #f0f0f0' }}>
                Schwimmer
              </div>
              {(gruppe.mitglieder || []).length === 0 && (
                <div style={{ padding: '1rem', color: '#999', fontSize: '0.85rem' }}>Keine Mitglieder</div>
              )}
              {(gruppe.mitglieder || []).map((m, i) => (
                <div key={m.schwimmer_id}
                  onClick={() => navigate(`/trainer/schwimmer/${m.schwimmer_id}`)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '0.75rem',
                    padding: '0.75rem 1rem', cursor: 'pointer',
                    borderBottom: i < gruppe.mitglieder.length - 1 ? '1px solid #f5f5f5' : 'none',
                    background: '#fff',
                  }}>
                  <div style={{
                    width: 36, height: 36, borderRadius: '50%', background: '#e3f2fd',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontWeight: 700, color: '#1565c0', fontSize: '0.85rem', flexShrink: 0,
                  }}>
                    {m.vorname?.[0]}{m.nachname?.[0]}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: '0.95rem' }}>{m.vorname} {m.nachname}</div>
                    <TrainerAntragBadge s={m} />
                    {m.geburtsdatum && (
                      <div style={{ fontSize: '0.75rem', color: '#888' }}>
                        * {new Date(m.geburtsdatum).toLocaleDateString('de-DE')}
                      </div>
                    )}
                  </div>
                  <span style={{ color: '#bbb', fontSize: '1.1rem' }}>›</span>
                </div>
              ))}
            </div>

            {/* Letzte Einheiten */}
            {(gruppe.einheiten || []).length > 0 && (
              <div style={{ background: '#fff', borderRadius: 12, boxShadow: '0 1px 4px rgba(0,0,0,0.07)', overflow: 'hidden' }}>
                <div style={{ padding: '0.75rem 1rem', fontWeight: 700, fontSize: '0.9rem', borderBottom: '1px solid #f0f0f0' }}>
                  Letzte Einheiten
                </div>
                {gruppe.einheiten.slice(0, 8).map((e, i) => (
                  <div key={e.id}
                    onClick={() => navigate(`/trainer/einheiten/${e.id}`)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '0.75rem',
                      padding: '0.75rem 1rem', cursor: 'pointer',
                      borderBottom: i < Math.min(gruppe.einheiten.length, 5) - 1 ? '1px solid #f5f5f5' : 'none',
                    }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>
                        {fmtDatum(e.datum)}
                      </div>
                      <div style={{ fontSize: '0.75rem', color: '#888' }}>
                        erfasst um {fmtUhrzeit(e.gestartet_am)} Uhr{e.gestartet_von_name ? ` · ${e.gestartet_von_name}` : ''}
                      </div>
                    </div>
                    <span style={{
                      padding: '2px 10px', borderRadius: 20, fontSize: '0.72rem', fontWeight: 700,
                      background: '#e8f5e9', color: '#2e7d32', whiteSpace: 'nowrap',
                    }}>
                      {e.anwesend} anwesend{e.team ? ` · ${e.team} im Team` : ''}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </>)}

          {tab === 'fortschritt' && (
            <div style={{ background: '#fff', borderRadius: 12, boxShadow: '0 1px 4px rgba(0,0,0,0.07)', overflow: 'hidden' }}>
              {fortschrittLoading && (
                <div style={{ textAlign: 'center', padding: '2rem' }}>
                  <div className="loading-spinner" style={{ margin: '0 auto' }} />
                </div>
              )}
              {!fortschrittLoading && fortschritt && (
                fortschritt.uebungen.length === 0 ? (
                  <div style={{ padding: '1rem', color: '#888', fontSize: '0.85rem' }}>
                    Keine Übungen für diesen Kurstyp hinterlegt.
                  </div>
                ) : fortschritt.mitglieder.length === 0 ? (
                  <div style={{ padding: '1rem', color: '#888', fontSize: '0.85rem' }}>Keine Mitglieder.</div>
                ) : (<>
                  <div style={{ padding: '0.6rem 1rem', fontSize: '0.78rem', color: '#666', borderBottom: '1px solid #f0f0f0' }}>
                    Tippe auf ○, um eine Übung abzuhaken. Ein Tipp auf ✓ nimmt sie wieder zurück.
                  </div>
                  {fortschrittFehler && (
                    <div className="alert alert-error" style={{ margin: '0.6rem 1rem' }}>{fortschrittFehler}</div>
                  )}
                  <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                    <table style={{ borderCollapse: 'collapse', fontSize: '0.75rem', width: '100%' }}>
                      <thead>
                        <tr>
                          <th style={{ padding: '0.5rem', background: '#f5f5f5', textAlign: 'left',
                            position: 'sticky', left: 0, zIndex: 1, borderBottom: '2px solid #e0e0e0',
                            minWidth: 110, fontWeight: 700, verticalAlign: 'bottom',
                            textTransform: 'none', letterSpacing: 0 }}>
                            Schwimmer
                          </th>
                          {/* Übungsnamen senkrecht: voll lesbar, Spalten bleiben schmal genug fürs Handy */}
                          {fortschritt.uebungen.map(u => (
                            <th key={u.id} title={u.titel}
                              style={{ padding: '0.5rem 0.15rem', background: '#f5f5f5',
                                textAlign: 'center', verticalAlign: 'bottom', borderBottom: '2px solid #e0e0e0',
                                width: 44, minWidth: 44 }}>
                              <span style={{
                                display: 'inline-block', writingMode: 'vertical-rl', transform: 'rotate(180deg)',
                                maxHeight: 170, whiteSpace: 'normal', textAlign: 'left',
                                fontSize: '0.8rem', fontWeight: 600, lineHeight: 1.15, color: '#333',
                                textTransform: 'none', letterSpacing: 0,
                              }}>
                                {u.titel}
                              </span>
                            </th>
                          ))}
                          <th style={{ padding: '0.4rem 0.3rem', background: '#f5f5f5',
                            textAlign: 'center', verticalAlign: 'bottom', borderBottom: '2px solid #e0e0e0', minWidth: 48,
                            textTransform: 'none', letterSpacing: 0 }}>
                            ✓
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {fortschritt.mitglieder.map((m, mi) => {
                          const done = Object.keys(m.abgeschlossen).length;
                          const total = fortschritt.uebungen.length;
                          return (
                            <tr key={m.schwimmer_id} style={{ background: mi % 2 === 0 ? '#fff' : '#fafafa' }}>
                              <td style={{ padding: '0.5rem', fontWeight: 600, position: 'sticky', left: 0,
                                background: mi % 2 === 0 ? '#fff' : '#fafafa',
                                borderBottom: '1px solid #eee', cursor: 'pointer', color: '#1565c0' }}
                                onClick={() => navigate(`/trainer/schwimmer/${m.schwimmer_id}`)}>
                                {m.vorname} {m.nachname}
                              </td>
                              {fortschritt.uebungen.map(u => {
                                const erledigt = m.abgeschlossen[u.id];
                                const beschaeftigt = !!inArbeit[`${m.schwimmer_id}_${u.id}`];
                                return (
                                  <td key={u.id} style={{ textAlign: 'center', padding: 0, borderBottom: '1px solid #eee' }}>
                                    <button type="button" onClick={() => toggleUebung(m, u)} disabled={beschaeftigt}
                                      title={erledigt
                                        ? `${u.titel}: abgehakt am ${new Date(erledigt).toLocaleDateString('de-DE')}`
                                        : `${u.titel}: noch offen`}
                                      aria-label={`${u.titel} bei ${m.vorname} ${m.nachname} ${erledigt ? 'zurücknehmen' : 'abhaken'}`}
                                      style={{
                                        width: '100%', minHeight: 42, border: 'none', background: 'transparent',
                                        cursor: 'pointer', padding: '0.3rem 0.2rem', opacity: beschaeftigt ? 0.5 : 1,
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                      }}>
                                      {erledigt
                                        ? <span style={{ color: '#2e7d32', fontSize: '1.15rem', fontWeight: 700 }}>✓</span>
                                        : <span style={{ display: 'inline-block', width: 18, height: 18, borderRadius: '50%', border: '2px solid #bdbdbd' }} />}
                                    </button>
                                  </td>
                                );
                              })}
                              <td style={{ textAlign: 'center', padding: '0.4rem', borderBottom: '1px solid #eee',
                                fontWeight: 700, fontSize: '0.8rem',
                                color: done === total ? '#2e7d32' : done > 0 ? '#e65100' : '#bbb' }}>
                                {done}/{total}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </>)
              )}
            </div>
          )}
        </>
      )}
    </TrainerLayout>
  );
}
